import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Pencil, Trash2, GripVertical, RotateCcw, XCircle, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAILABLE_ICONS = ['Home', 'Building', 'Building2', 'Briefcase', 'Layers', 'Box', 'Boxes'];

// Project Types Tab — admin CRUD + drag-and-drop reorder of the wizard catalog
const ProjectTypesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '',
    label: '',
    description: '',
    icon: 'Building',
    requires_building_plans: true,
    requires_building_picker: false,
    enabled: true,
  });
  const [busy, setBusy] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/project-types`, { withCredentials: true });
      setItems(r.data || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      code: '',
      label: '',
      description: '',
      icon: 'Building',
      requires_building_plans: true,
      requires_building_picker: false,
      enabled: true,
    });
    setShowForm(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      code: it.code,
      label: it.label,
      description: it.description || '',
      icon: it.icon || 'Building',
      requires_building_plans: !!it.requires_building_plans,
      requires_building_picker: !!it.requires_building_picker,
      enabled: !!it.enabled,
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.label.trim() || !form.code.trim()) {
      toast.error('Code et libellé requis');
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const { code, ...payload } = form;
        await axios.put(`${API}/admin/project-types/${editing.project_type_id}`, payload, { withCredentials: true });
        toast.success(`Type "${form.label}" mis à jour`);
      } else {
        await axios.post(`${API}/admin/project-types`, form, { withCredentials: true });
        toast.success(`Type "${form.label}" créé`);
      }
      setShowForm(false);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (it) => {
    try {
      if (it.enabled) {
        await axios.delete(`${API}/admin/project-types/${it.project_type_id}`, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/project-types/${it.project_type_id}`, { enabled: true }, { withCredentials: true });
      }
      toast.success(it.enabled ? 'Type désactivé' : 'Type réactivé');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const onDragStart = (idx) => setDraggedIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (targetIdx) => {
    if (draggedIdx === null || draggedIdx === targetIdx) return;
    const reordered = [...items];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setItems(reordered);
    setDraggedIdx(null);
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if (it.order !== i + 1) {
            return axios.put(
              `${API}/admin/project-types/${it.project_type_id}`,
              { order: i + 1 },
              { withCredentials: true }
            );
          }
          return null;
        }).filter(Boolean)
      );
      toast.success('Ordre mis à jour');
      await fetchAll();
    } catch {
      toast.error('Échec de la sauvegarde de l\'ordre');
    }
  };

  return (
    <div data-testid="project-types-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Catalogue des types de projet</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Glissez-déposez pour réordonner. Les types désactivés ne sont plus proposés dans le wizard.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="add-project-type-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau type
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm" data-testid="project-types-empty">
          Aucun type de projet. Cliquez sur "Nouveau type" pour en créer un.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-3 w-8"></th>
                <th className="text-left px-4 py-3">Code / Libellé</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Description</th>
                <th className="text-left px-4 py-3">Flags</th>
                <th className="text-left px-4 py-3">État</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr
                  key={it.project_type_id}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(idx)}
                  className={`border-t border-gray-100 hover:bg-gray-50 cursor-move ${
                    draggedIdx === idx ? 'opacity-40' : ''
                  } ${!it.enabled ? 'opacity-60' : ''}`}
                  data-testid={`project-type-row-${it.code}`}
                >
                  <td className="px-3 py-3 text-gray-400">
                    <GripVertical className="w-4 h-4" />
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{it.label}</p>
                    <p className="text-[11px] text-gray-400 font-mono">{it.code} · {it.icon}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs text-gray-600 line-clamp-2">{it.description || '—'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      {it.requires_building_picker && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200">Picker</span>
                      )}
                      {it.requires_building_plans && (
                        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">Plans</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${
                      it.enabled
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200'
                    }`}>
                      {it.enabled ? 'Actif' : 'Désactivé'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(it)}
                        className="h-8 w-8 p-0 text-gray-600 hover:text-blue-600"
                        data-testid={`edit-project-type-${it.code}`}
                        title="Modifier"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleEnabled(it)}
                        className={`h-8 w-8 p-0 ${it.enabled ? 'text-red-500 hover:text-red-700' : 'text-emerald-500 hover:text-emerald-700'}`}
                        data-testid={`toggle-project-type-${it.code}`}
                        title={it.enabled ? 'Désactiver' : 'Réactiver'}
                      >
                        {it.enabled ? <Trash2 className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-[1500] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => !busy && setShowForm(false)}
          data-testid="project-type-form-modal"
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                {editing ? 'Modifier le type' : 'Nouveau type de projet'}
              </h3>
              <button onClick={() => setShowForm(false)} disabled={busy} className="text-gray-400 hover:text-gray-700">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Code (immuable)</label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  disabled={!!editing}
                  placeholder="ex. apartment_premium"
                  className="font-mono text-sm h-10"
                  data-testid="project-type-code-input"
                />
                <p className="text-[10px] text-gray-400 mt-1">Lettres minuscules, chiffres et underscores uniquement.</p>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Libellé</label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value.slice(0, 80) })}
                  placeholder="ex. Appartement haut de gamme"
                  className="h-10"
                  data-testid="project-type-label-input"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 240) })}
                  rows={2}
                  className="w-full bg-white border border-gray-200 px-3 py-2 text-sm rounded-lg resize-none focus:outline-none focus:border-blue-400"
                  data-testid="project-type-description-input"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-1.5">Icône</label>
                <select
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  className="w-full h-10 px-3 bg-white border border-gray-200 rounded-lg text-sm"
                  data-testid="project-type-icon-select"
                >
                  {AVAILABLE_ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requires_building_picker}
                    onChange={(e) => setForm({ ...form, requires_building_picker: e.target.checked })}
                    data-testid="project-type-picker-checkbox"
                  />
                  <span className="text-xs text-gray-700">Sélection bâtiment</span>
                </label>
                <label className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.requires_building_plans}
                    onChange={(e) => setForm({ ...form, requires_building_plans: e.target.checked })}
                    data-testid="project-type-plans-checkbox"
                  />
                  <span className="text-xs text-gray-700">Plans requis</span>
                </label>
              </div>
              <label className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  data-testid="project-type-enabled-checkbox"
                />
                <span className="text-xs text-gray-700">Visible dans le wizard</span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50">
              <Button variant="ghost" onClick={() => setShowForm(false)} disabled={busy}>Annuler</Button>
              <Button
                onClick={submit}
                disabled={busy || !form.label.trim() || !form.code.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="project-type-submit-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {editing ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTypesTab;
